import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Help, MenuBook, BugReport, WhatsApp, Close } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import apiClient from '@services/api';
import { useAuth } from '@context/AuthContext';
import { recentApiErrors } from '../../utils/recentErrors';
import { guidesFor } from './helpGuides';
import { useT } from '@context/PreferencesContext';

// MedEase support WhatsApp number (digits with country code), set at build time; hidden when empty
const SUPPORT_WHATSAPP = (import.meta.env.VITE_SUPPORT_WHATSAPP as string | undefined)?.replace(/\D/g, '') || '';

/** "?" in the top bar: a quick guide for your role, report a problem, WhatsApp support. */
const HelpMenu: React.FC = () => {
  const { user } = useAuth();
  const t = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      await apiClient.post('/api/support/reports', { message, page: location.pathname, recentErrors: recentApiErrors() });
      enqueueSnackbar('Thanks — the MedEase team has your report and will get back to you.', { variant: 'success' });
      setReportOpen(false);
      setMessage('');
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.error || 'Could not send. Please try WhatsApp support.', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  const guides = guidesFor(user?.supportMode ? 'admin' : user?.role);

  return (
    <>
      <Tooltip title={t('help.title')}>
        <IconButton sx={{ color: 'text.secondary' }} onClick={(e) => setAnchor(e.currentTarget)} aria-label="Help and support">
          <Help />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => { setAnchor(null); setGuideOpen(true); }}>
          <ListItemIcon><MenuBook fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('help.guide')} secondary={t('help.guideSub')} />
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); setReportOpen(true); }}>
          <ListItemIcon><BugReport fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('help.report')} secondary={t('help.reportSub')} />
        </MenuItem>
        {SUPPORT_WHATSAPP && (
          <MenuItem
            component="a"
            href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(`Hi MedEase support, I'm ${user?.firstName ?? ''} from ${user?.organizationName ?? 'my clinic'}. `)}`}
            target="_blank"
            rel="noopener"
            onClick={() => setAnchor(null)}
          >
            <ListItemIcon><WhatsApp fontSize="small" color="success" /></ListItemIcon>
            <ListItemText primary={t('help.whatsapp')} secondary={t('help.whatsappSub')} />
          </MenuItem>
        )}
      </Menu>

      <Drawer anchor="right" open={guideOpen} onClose={() => setGuideOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={600}>Quick guide</Typography>
          <IconButton onClick={() => setGuideOpen(false)} aria-label="Close guide"><Close /></IconButton>
        </Box>
        <Divider />
        <Box sx={{ p: 2, overflowY: 'auto' }}>
          {guides.map((g) => (
            <Box key={g.title} sx={{ mb: 3 }}>
              <Typography fontWeight={600} gutterBottom>{g.title}</Typography>
              <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                {g.steps.map((s) => (
                  <Typography component="li" variant="body2" key={s} sx={{ mb: 0.5 }}>{s}</Typography>
                ))}
              </Box>
              {g.link && (
                <Button size="small" sx={{ mt: 0.5 }} onClick={() => { setGuideOpen(false); navigate(g.link!.path); }}>
                  {g.link.label} →
                </Button>
              )}
            </Box>
          ))}
          <Alert severity="info">Stuck? Use Help → Report a problem{SUPPORT_WHATSAPP ? ' or WhatsApp support' : ''}.</Alert>
        </Box>
      </Drawer>

      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Report a problem</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={4}
            autoFocus
            label="What happened?"
            placeholder="e.g. I pressed Save & sign and nothing happened"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            inputProps={{ maxLength: 4000 }}
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            We also send the page you're on and the last few errors (no patient details), so we can find the problem quickly.
            Please don't type patient names or phone numbers here.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={send} disabled={sending || message.trim().length < 5}>
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HelpMenu;
